import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { generateIdempotencyKey } from '../../core/utils/idempotency-key.util';
import {
  CompleteTimeBlockRequest,
  SkipTimeBlockRequest,
  StartTimeBlockRequest,
  TimeBlockResponse
} from './time-block.model';

/**
 * Service for managing time blocks related to today's activities.
 * Provides methods to start, complete, and skip time blocks.
 * Each method supports caller-provided idempotency keys for safe retries.
 *
 * @author sadatmalik
 */
@Injectable({ providedIn: 'root' })
export class TimeBlockService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/time-blocks`;

  start(request: StartTimeBlockRequest, idempotencyKey?: string): Observable<TimeBlockResponse> {
    return this.http.post<TimeBlockResponse>(`${this.baseUrl}/start`, request, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  complete(
    blockId: string,
    request: CompleteTimeBlockRequest,
    idempotencyKey?: string
  ): Observable<TimeBlockResponse> {
    return this.http.post<TimeBlockResponse>(`${this.baseUrl}/${blockId}/complete`, request, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  skip(
    blockId: string,
    request: SkipTimeBlockRequest,
    idempotencyKey?: string
  ): Observable<TimeBlockResponse> {
    return this.http.post<TimeBlockResponse>(`${this.baseUrl}/${blockId}/skip`, request, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  pause(blockId: string, idempotencyKey?: string): Observable<TimeBlockResponse> {
    return this.http.post<TimeBlockResponse>(`${this.baseUrl}/${blockId}/pause`, {}, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  resume(blockId: string, idempotencyKey?: string): Observable<TimeBlockResponse> {
    return this.http.post<TimeBlockResponse>(`${this.baseUrl}/${blockId}/resume`, {}, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  private idempotencyHeaders(idempotencyKey?: string): HttpHeaders {
    const normalizedKey = idempotencyKey?.trim();
    const key = normalizedKey && normalizedKey.length > 0 ? normalizedKey : generateIdempotencyKey();
    return new HttpHeaders({
      'Idempotency-Key': key
    });
  }
}
